import React, { FC } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Button } from '@bphxd/ds-core-react';
import { EMPTY_INSULATION } from '../../../lib/constants';
import { TInsulation } from '../../../lib/types';
import { useSelectedAsset } from '../../../lib/util';
import { renderOptions } from '../../../lib/util/render';
import {
  INSULATION_HEAT_TRACING_ENUM,
  INSULATION_MATERIALS_ENUM,
  INSULATION_STATUS_ENUM,
} from '../../../lib/constants/dbConstants';
import {
  InsulationHeatTracingEnum,
  InsulationMaterialsEnum,
  InsulationStatusEnum,
} from '../../../graph-queries/__generated__/graphql';

const InsulationForm: FC<{
  defaultValues?: TInsulation;
  onSubmit: (insulation: TInsulation) => Promise<unknown>;
  onClose: () => void;
  type: 'add' | 'edit';
  isLoading?: boolean;
}> = ({ defaultValues, onSubmit, type, onClose, isLoading }) => {
  const asset = useSelectedAsset();
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    const data = new FormData(event.target as HTMLFormElement);
    const inputData = Object.fromEntries(data) as Record<string, string> & {
      materials: InsulationMaterialsEnum;
      heatTracing: InsulationHeatTracingEnum;
      status: InsulationStatusEnum;
    };

    const insulation: TInsulation = {
      ...EMPTY_INSULATION,
      ...inputData,
      assetId: asset?.assetId || -1,
    };
    if (defaultValues?.insulationId) insulation.insulationId = defaultValues.insulationId;

    onSubmit(insulation)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };
  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-6">
        <FormGroup>
          <Label for="insulation-reference">Plan Reference</Label>
          <Input
            type="text"
            name="referenceNumber"
            id="liftplan-reference"
            required
            defaultValue={defaultValues?.referenceNumber || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-12">
        <FormGroup>
          <Label for="insulation-description">Insulation Description</Label>
          <Input
            type="text"
            name="description"
            id="insulation-description"
            required
            defaultValue={defaultValues?.description || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="insulation-circuit">Circuit Number</Label>
          <Input
            type="text"
            name="circuitNumber"
            id="insulation-circuit"
            required
            defaultValue={defaultValues?.circuitNumber || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="insulation-heat-tracing">Heat Pack</Label>
          <Input
            type="select"
            name="heatTracing"
            id="insulation-heat-tracing"
            required
            role="combobox"
            defaultValue={defaultValues?.heatTracing || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(INSULATION_HEAT_TRACING_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="insulation-materials">Materials</Label>
          <Input
            type="select"
            name="materials"
            id="insulation-materials"
            required
            role="combobox"
            defaultValue={defaultValues?.materials || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(INSULATION_MATERIALS_ENUM)}
          </Input>
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="insulation-status">Status</Label>
          <Input
            type="select"
            name="status"
            id="insulation-status"
            required
            role="combobox"
            defaultValue={defaultValues?.status || ''}
          >
            <option disabled hidden value="" key="_placeholder_">
              Choose an option
            </option>
            {renderOptions(INSULATION_STATUS_ENUM)}
          </Input>
        </FormGroup>
      </div>

      <div className="col-md-12">
        <FormGroup>
          <Label for="insulation-comments" className="label-optional">
            Comments
          </Label>
          <Input
            type="textarea"
            name="comments"
            id="insulation-comments"
            defaultValue={defaultValues?.comments || ''}
          />
        </FormGroup>
      </div>
      {type === 'add' ? (
        <div className="col-md-12">
          <Button
            type="submit"
            size="lg"
            className="w-100 my-4"
            theme="darker"
            level="primary"
            disabled={isLoading}
          >
            Add new Insulation
          </Button>
        </div>
      ) : (
        <>
          <div className="col-md-6">
            <Button
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="tertiary"
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
          <div className="col-md-6">
            <Button
              type="submit"
              size="lg"
              className="w-100 my-4"
              theme="darker"
              level="primary"
              disabled={isLoading}
            >
              Save changes
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};

export default InsulationForm;
